/**
 * Master Tour Calendar month/week day cards.
 * Pure helpers — no React, no Firestore. Kind is derived from existing tour-day data
 * (venue vs travel vs in-range off). Live flight status is intentionally out of scope.
 */

export const DAY_KIND = {
  SHOW: 'show',
  TRAVEL: 'travel',
  OFF: 'off',
};

export const DAY_KIND_LABEL = {
  show: 'Show',
  travel: 'Travel',
  off: 'Off',
};

/** v2 Master Tour Calendar left-bar accents (teal / amber / stone). */
export const DAY_KIND_ACCENT = {
  show: '#2a7d5c',
  travel: '#c08133',
  off: '#9a9a94',
};

export function accentForDayKind(kind, fallback = null) {
  return DAY_KIND_ACCENT[kind] || fallback || null;
}

/** Prototype month cards: 3px left bar only on show days. Travel/off keep a 1px hairline. */
export function leftBarColorForKind(kind) {
  return kind === DAY_KIND.SHOW ? DAY_KIND_ACCENT.show : null;
}

export function pickTourDayCard(meta, selectedTourFilterId) {
  if (!meta) return null;
  const tours = Array.isArray(meta.tours) ? meta.tours : [];
  if (selectedTourFilterId) {
    const hit = tours.find((t) => String(t.tourId) === String(selectedTourFilterId));
    if (hit?.card) return hit.card;
  }
  return tours.find((t) => t?.card)?.card || meta.card || null;
}

export function classifyTourDayKind({ hasShowVenue = false, hasTravel = false, inTourRange = false } = {}) {
  if (hasShowVenue) return DAY_KIND.SHOW;
  if (hasTravel) return DAY_KIND.TRAVEL;
  if (inTourRange) return DAY_KIND.OFF;
  return null;
}

export function formatPlaceLine({ city, state, name } = {}) {
  const c = String(city || '').trim();
  const s = String(state || '').trim();
  if (c && s) return `${c}, ${s}`;
  if (c) return c;
  if (s) return s;
  const fromName = String(name || '').trim();
  const m = fromName.match(/,\s*([^,]+,\s*[A-Z]{2})\s*$/);
  if (m) return m[1].trim();
  return '';
}

export function formatCapacityChip(capacity) {
  if (capacity == null || capacity === '') return null;
  const n = parseInt(String(capacity).replace(/[^\d]/g, ''), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n.toLocaleString()} cap`;
}

export function formatTravelLine({ fromLabel, toLabel, modeLabel } = {}) {
  const from = String(fromLabel || '').trim();
  const to = String(toLabel || '').trim();
  if (!from || !to || from === to) return '';
  const mode = String(modeLabel || '').trim();
  return mode ? `${from} → ${to}` : `${from} → ${to}`;
}

/**
 * @returns {{ kind: string|null, kindLabel: string, cityLine: string, venueLine: string, chips: Array<{text: string, tone: string}> }}
 */
export function buildTourDayCard({
  inTourRange = false,
  venueName = '',
  city = '',
  state = '',
  capacity = '',
  hasHotel = false,
  travel = null,
} = {}) {
  const name = String(venueName || '').trim();
  const place = formatPlaceLine({ city, state, name });
  const hasTravel = Boolean(travel && !travel.same && (travel.fromLabel || travel.toLabel));
  const hasShowVenue = Boolean(name);
  const kind = classifyTourDayKind({ hasShowVenue, hasTravel, inTourRange });
  const travelLine = hasTravel ? formatTravelLine(travel) : '';

  let cityLine = place;
  let venueLine = name;
  if (kind === DAY_KIND.TRAVEL) {
    cityLine = place || String(travel?.toLabel || '').trim();
    venueLine = travelLine || (travel?.modeLabel ? String(travel.modeLabel) : name);
  } else if (kind === DAY_KIND.OFF) {
    cityLine = place;
    venueLine = hasHotel ? 'Rooms held · no show' : (name || 'Day off');
  }

  const chips = [];
  if (kind === DAY_KIND.SHOW) {
    const cap = formatCapacityChip(capacity);
    if (cap) chips.push({ text: cap, tone: 'muted' });
  }
  if (kind && hasHotel && chips.length < 2) {
    chips.push({ text: 'Hotel', tone: 'muted', action: 'lodging' });
  }

  return {
    kind,
    kindLabel: kind ? DAY_KIND_LABEL[kind] : '',
    cityLine: cityLine || '',
    venueLine: venueLine || '',
    chips,
    travelLine,
    hasHotel: Boolean(hasHotel),
  };
}
